import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { QrCode, Scan, RefreshCw, Check, X, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function QRAttendance() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [mode, setMode] = useState('scan'); // 'scan' or 'generate'
  const [qrCode, setQrCode] = useState(null);
  const [scanToken, setScanToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [action, setAction] = useState('check_in');
  const [validUntil, setValidUntil] = useState(null);

  const isAdmin = ['super_admin', 'company_admin', 'hr'].includes(user?.role);

  const generateQR = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/qr/generate?action=${action}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setQrCode(response.data.qr_code);
      setValidUntil(new Date(response.data.valid_until));
      toast.success(t('attendance.qrGenerate') + ' ✓');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur génération QR');
    } finally {
      setLoading(false);
    }
  };

  const scanQR = async () => {
    if (!scanToken.trim()) {
      toast.error('Veuillez entrer le token QR');
      return;
    }
    
    setLoading(true);
    setScanResult(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/qr/scan?token=${encodeURIComponent(scanToken)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setScanResult(response.data);
      toast.success(t('attendance.qrSuccess'));
      setScanToken('');
    } catch (error) {
      setScanResult({ success: false, message: error.response?.data?.detail || 'Erreur scan' });
      toast.error(error.response?.data?.detail || 'QR invalide ou expiré');
    } finally {
      setLoading(false);
    }
  };

  // Countdown timer for QR validity
  useEffect(() => {
    if (!validUntil) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      if (now >= validUntil) {
        setQrCode(null);
        setValidUntil(null);
        toast.info('QR expiré, veuillez en générer un nouveau');
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [validUntil]);

  const getRemainingTime = () => {
    if (!validUntil) return null;
    const diff = Math.max(0, Math.floor((validUntil - new Date()) / 1000));
    const mins = Math.floor(diff / 60);
    const secs = diff % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
          <QrCode className="w-7 h-7 text-primary" />
          {t('nav.qrAttendance')}
        </h1>
        <p className="text-slate-600">{t('attendance.qrScan')} / {t('attendance.qrGenerate')}</p>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={mode === 'generate' ? 'default' : 'outline'}
          onClick={() => setMode('generate')}
          className="gap-2"
        >
          <QrCode className="w-4 h-4" />
          Générer mon QR
        </Button>
        {isAdmin && (
          <Button
            variant={mode === 'scan' ? 'default' : 'outline'}
            onClick={() => setMode('scan')}
            className="gap-2"
          >
            <Scan className="w-4 h-4" />
            Scanner (Admin)
          </Button>
        )}
      </div>

      {/* Generate Mode */}
      {mode === 'generate' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-primary" />
              Générer un QR Code de pointage
            </CardTitle>
            <CardDescription>
              Présentez ce QR code à un administrateur pour enregistrer votre pointage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4 items-center">
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="check_in">🟢 Arrivée (Entrée)</option>
                <option value="break_start">⏸️ Début de pause</option>
                <option value="break_end">▶️ Fin de pause (Retour)</option>
                <option value="check_out">🔴 Départ (Sortie)</option>
              </select>
              <Button onClick={generateQR} disabled={loading} className="gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Générer
              </Button>
            </div>

            {qrCode && (
              <div className="flex flex-col items-center gap-4 p-6 bg-white rounded-lg border">
                <img src={qrCode} alt="QR Code" className="w-64 h-64" />
                {validUntil && (
                  <div className="flex items-center gap-2 text-amber-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-mono text-lg">{getRemainingTime()}</span>
                    <span className="text-sm">restant</span>
                  </div>
                )}
                <p className="text-sm font-medium text-slate-700">
                  {{
                    check_in: '🟢 Arrivée (Entrée)',
                    break_start: '⏸️ Début de pause',
                    break_end: '▶️ Fin de pause (Retour)',
                    check_out: '🔴 Départ (Sortie)',
                  }[action] || action}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Scan Mode (Admin only) */}
      {mode === 'scan' && isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-primary" />
              Scanner un QR Code
            </CardTitle>
            <CardDescription>
              Entrez ou scannez le token QR d'un employé pour enregistrer son pointage
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Coller le token QR ici..."
                value={scanToken}
                onChange={(e) => setScanToken(e.target.value)}
                className="flex-1 font-mono text-sm"
              />
              <Button onClick={scanQR} disabled={loading || !scanToken} className="gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scan className="w-4 h-4" />}
                Scanner
              </Button>
            </div>

            {scanResult && (
              <div className={`p-4 rounded-lg ${scanResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
                <div className="flex items-center gap-2">
                  {scanResult.success ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-medium ${scanResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {scanResult.message}
                  </span>
                </div>
                {scanResult.success && (
                  <div className="mt-2 text-sm text-slate-600">
                    <p><strong>Employé:</strong> {scanResult.employee_name}</p>
                    <p><strong>Heure:</strong> {scanResult.time}</p>
                    <p><strong>Action:</strong> {{
                      check_in: 'Arrivée',
                      break_start: 'Début pause',
                      break_end: 'Fin pause',
                      check_out: 'Sortie',
                    }[scanResult.action] || scanResult.action}</p>
                    {scanResult.is_late && (
                      <p className="text-amber-600">
                        <strong>Retard:</strong> {scanResult.late_minutes} minutes
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <QrCode className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Comment ça marche ?</p>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>Les employés génèrent un QR code temporaire (valide 5 min)</li>
                <li>Un administrateur/RH scanne le QR pour valider le pointage</li>
                <li>Alternative au pointeur biométrique en cas de panne</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
